// Reads a ZIP of product photos in the browser without unpacking all of it:
// the list of files comes from the ZIP's directory at its end, and each photo
// is read (and inflated) only when it is needed. A ZIP of thousands of photos
// (hundreds of MB) never sits in memory whole.

export type ZipEntry = { name: string; method: number; compressedSize: number; size: number; localOffset: number };

const EOCD = 0x06054b50;
const CENTRAL = 0x02014b50;
const LOCAL = 0x04034b50;

/** Every file in the ZIP (folders left out). Throws on a file that is not a ZIP, or a ZIP64 one. */
export async function zipEntries(file: Blob): Promise<ZipEntry[]> {
  const tailSize = Math.min(file.size, 65_557);
  const tail = new DataView(await file.slice(file.size - tailSize).arrayBuffer());
  let at = -1;
  for (let i = tailSize - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) === EOCD) {
      at = i;
      break;
    }
  }
  if (at < 0) throw new Error("This is not a ZIP file.");
  const count = tail.getUint16(at + 10, true);
  const dirSize = tail.getUint32(at + 12, true);
  const dirOffset = tail.getUint32(at + 16, true);
  if (count === 0xffff || dirOffset === 0xffffffff) throw new Error("ZIP64 files are not supported: make the ZIP smaller than 4 GB and 65,000 files.");
  const dir = new DataView(await file.slice(dirOffset, dirOffset + dirSize).arrayBuffer());
  const utf8 = new TextDecoder("utf-8");
  const out: ZipEntry[] = [];
  let p = 0;
  while (p + 46 <= dir.byteLength && dir.getUint32(p, true) === CENTRAL) {
    const method = dir.getUint16(p + 10, true);
    const compressedSize = dir.getUint32(p + 20, true);
    const size = dir.getUint32(p + 24, true);
    const nameLen = dir.getUint16(p + 28, true);
    const extraLen = dir.getUint16(p + 30, true);
    const commentLen = dir.getUint16(p + 32, true);
    const localOffset = dir.getUint32(p + 42, true);
    const name = utf8.decode(new Uint8Array(dir.buffer, dir.byteOffset + p + 46, nameLen));
    if (!name.endsWith("/")) out.push({ name, method, compressedSize, size, localOffset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

/** One file's bytes. Stored and deflated files are read; other methods throw. */
export async function zipRead(file: Blob, entry: ZipEntry): Promise<Blob> {
  const head = new DataView(await file.slice(entry.localOffset, entry.localOffset + 30).arrayBuffer());
  if (head.getUint32(0, true) !== LOCAL) throw new Error(`Bad ZIP entry: ${entry.name}`);
  const start = entry.localOffset + 30 + head.getUint16(26, true) + head.getUint16(28, true);
  const data = file.slice(start, start + entry.compressedSize);
  if (entry.method === 0) return data;
  if (entry.method !== 8) throw new Error(`Unsupported compression in ${entry.name}`);
  return new Response(data.stream().pipeThrough(new DecompressionStream("deflate-raw"))).blob();
}

/** "images/Napa 500.JPG" → "napa 500.jpg", as the server compares names. */
export const baseName = (path: string) => (path.split(/[\\/]/).pop() ?? "").trim().toLowerCase();
